import { useState, useEffect, useCallback, useMemo } from 'react';
import CartHandler, { dedupeCartItems } from '../CartHandler';
import { resolveApiUrl } from '../utils/api';

const STORAGE_KEY = 'gatsbyWooCart';
const SHIPPING_KEY = 'gatsbyWooCartShipping';
const SHIPPING_OPTIONS_KEY = 'gatsbyWooCartShippingOptions';
const NO_SHIPPING_OPTIONS_MESSAGE = "We don't offer shipping to this area";

const useLocalCart = () => {
  // Restore browser data after hydration. Writes stay disabled until it is read.
  const [canUseStorage, setCanUseStorage] = useState(false);
  const [cartData, setCartData] = useState([]);
  const [cartUser, setCartUser] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState(null);

  useEffect(() => {
    try {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
    } catch {
      return;
    }

    const readStoredValue = (key, fallback) => {
      try {
        const value = window.localStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
      } catch {
        return fallback;
      }
    };

    setCartData(dedupeCartItems(readStoredValue(STORAGE_KEY, [])));
    setShipping(readStoredValue(SHIPPING_KEY, null));
    setShippingOptions(readStoredValue(SHIPPING_OPTIONS_KEY, []));
    setCanUseStorage(true);
  }, []);

  const cartHandler = useMemo(
    () => new CartHandler(cartData, cartUser, coupons, shipping),
    [cartData, cartUser, coupons, shipping]
  );

  useEffect(() => {
    const normalizedCartData = dedupeCartItems(cartData);

    if (JSON.stringify(normalizedCartData) !== JSON.stringify(cartData)) {
      setCartData(normalizedCartData);
      return;
    }

    try {
      if (!canUseStorage) return;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedCartData));
    } catch (error) {
      // console.error('Error saving cart data to localStorage:', error);
    }
  }, [cartData, canUseStorage]);

  useEffect(() => {
    try {
      if (!canUseStorage) return;
      if (!shipping) {
        window.localStorage.removeItem(SHIPPING_KEY);
        return;
      }
      window.localStorage.setItem(SHIPPING_KEY, JSON.stringify(shipping));
    } catch (error) {
      // console.error('Error saving shipping data to localStorage:', error);
    }
  }, [shipping, canUseStorage]);

  useEffect(() => {
    try {
      if (!canUseStorage) return;
      window.localStorage.setItem(SHIPPING_OPTIONS_KEY, JSON.stringify(shippingOptions));
    } catch (error) {
      // console.error('Error saving shipping options to localStorage:', error);
    }
  }, [shippingOptions, canUseStorage]);

  const attachCustomer = useCallback((userData) => {
    setCartUser(userData);
  }, []);

  const addToCart = useCallback((product) => {
    cartHandler.addItem(product);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartHandler]);

  const removeFromCart = useCallback((productId) => {
    cartHandler.removeItem(productId);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartHandler]);

  const updateCartItem = useCallback((productId, quantity) => {
    cartHandler.updateItemQuantity(productId, quantity);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartHandler]);

  const emptyCart = useCallback(() => {
    cartHandler.clearCart();
    setCartData([]);
    setCoupons([]);
    setShipping(null);
    setShippingOptions([]);
    setShippingError(null);
    try {
      if (!canUseStorage) return;
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(SHIPPING_KEY);
      window.localStorage.removeItem(SHIPPING_OPTIONS_KEY);
    } catch (error) {
      // console.error('Error removing cart data from localStorage:', error);
    }
  }, [canUseStorage, cartHandler]);

  const applyCoupon = useCallback((couponData) => {
    cartHandler.applyCoupon(couponData);
    setCoupons(cartHandler.coupons);
  }, [cartHandler]);

  const removeCoupon = useCallback((code) => {
    cartHandler.removeCoupon(code);
    setCoupons(cartHandler.coupons);
  }, [cartHandler]);

  const checkout = useCallback(async (userData) => {
    return cartHandler.checkout(userData);
  }, [cartHandler]);

  const refreshShippingOptions = useCallback(async (addressData = null) => {
    if (!cartData.length) {
      cartHandler.clearShippingMethod();
      setShipping(null);
      setShippingOptions([]);
      return [];
    }

    setShippingLoading(true);
    setShippingError(null);

    try {
      const fallbackAddress = cartUser?.shipping || cartUser?.billing || {};
      const address = addressData || fallbackAddress;
      const subtotal = cartHandler.getSubtotal();
      const items = cartData.map((item) => ({
        productId: item?.id ?? null,
        variationId: item?.variation_id ?? null,
        quantity: item?.quantity ?? 1,
        subtotal: Number(item?.price || 0) * Math.max(1, Number.parseInt(item?.quantity, 10) || 1),
      }));

      const response = await fetch(resolveApiUrl('/api/woo/shipping-options'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          address,
          items,
          subtotal,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Failed to load shipping options');
      }

      const options = data?.options || [];
      setShippingOptions(options);

      if (!options.length) {
        cartHandler.clearShippingMethod();
        setShipping(null);
        setShippingError(NO_SHIPPING_OPTIONS_MESSAGE);
        return options;
      }

      const selectedShipping =
        options.find(option => option.rate_id === shipping?.rate_id) || options[0];

      cartHandler.setShippingMethod(selectedShipping);
      setShipping(cartHandler.shipping);

      return options;
    } catch (error) {
      setShippingError(error.message || 'Unable to load shipping options');
      return [];
    } finally {
      setShippingLoading(false);
    }
  }, [cartData, cartUser, cartHandler, shipping]);

  const selectShippingOption = useCallback((rateId) => {
    const selected = shippingOptions.find(option => option.rate_id === rateId);

    if (!selected) {
      throw new Error('Selected shipping option is not available');
    }

    cartHandler.setShippingMethod(selected);
    setShipping(cartHandler.shipping);
    setShippingError(null);
    return cartHandler.shipping;
  }, [shippingOptions, cartHandler]);

  const clearShippingSelection = useCallback(() => {
    cartHandler.clearShippingMethod();
    setShipping(null);
    setShippingError(null);
  }, [cartHandler]);

  return {
    cartData,
    customer: cartUser,
    coupons,
    shipping,
    shippingOptions,
    shippingLoading,
    shippingError,
    addToCart,
    removeFromCart,
    updateCartItem,
    emptyCart,
    attachCustomer,
    applyCoupon,
    removeCoupon,
    refreshShippingOptions,
    selectShippingOption,
    clearShippingSelection,
    checkout,
    getGrossCartTotal: useCallback(() => cartHandler.getGrossTotal(), [cartHandler]),
    getNetCartTotal: useCallback(() => cartHandler.getNetTotal(), [cartHandler]),
    getTotalItems: useCallback(() => cartHandler.getTotalItems(), [cartHandler]),
    isInCart: useCallback((productId) => cartHandler.isInCart(productId), [cartHandler]),
    getItemQuantity: useCallback((productId) => cartHandler.getItemQuantity(productId), [cartHandler]),
    getSubtotal: useCallback(() => cartHandler.getSubtotal(), [cartHandler]),
    getTotalDiscount: useCallback(() => cartHandler.getTotalDiscount(), [cartHandler]),
    getTaxAmount: useCallback(() => cartHandler.getTaxAmount(), [cartHandler]),
    getShippingCost: useCallback(() => cartHandler.getShippingCost(), [cartHandler])
  };
};

export default useLocalCart;
